import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementBlock as _createElementBlock, Fragment as _Fragment, resolveDirective as _resolveDirective, withDirectives as _withDirectives, vShow as _vShow } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "q-pa-md"
}
const _hoisted_2 = { class: "text-subtitle2 text-weight-regular text-grey-7" }
const _hoisted_3 = { class: "row no-wrap justify-between q-col-gutter-x-md" }
const _hoisted_4 = { class: "" }
const _hoisted_5 = { class: "text-h4 text-weight-medium" }
const _hoisted_6 = { class: "text-center column" }
const _hoisted_7 = { class: "q-py-md" }
const _hoisted_8 = {
  key: 0,
  class: "text-h6"
}
const _hoisted_9 = {
  key: 1,
  class: "text-body-2"
}
const _hoisted_10 = { class: "row" }
const _hoisted_11 = {
  key: 0,
  class: "row"
}
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { class: "q-pa-md text-center" }
const _hoisted_14 = { class: "row" }
const _hoisted_15 = { class: "full-width text-center" }

import {
  computed,
  DeepReadonly,
  inject,
  onBeforeUnmount,
  onBeforeUpdate,
  onErrorCaptured,
  provide,
  reactive,
  ref,
  watch,
  toRaw,
  nextTick,
  onMounted,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useTimers } from '../store/timers'
import { useFormatTime } from '../composables/formatTime'
import { useFormatDate } from '../composables/formatDate'
import { findLast } from 'lodash-es'
import { ChildTimer } from '../types/timers'
import { Task, useTaskProfiles } from '../store/useTaskProfiles'
import TaskBase from '../components/TaskBase.vue'
import RecapDefinition from '../components/recaps/RecapDefinition.vue'
import { useRecapStore } from '../store/useRecapStore'
import { useInventory } from '../store/useInventory'
import { captureException, setContext } from '@sentry/vue'
import { TaskModel } from '../models/TaskModel'
import { isNullish, logInfo, parseBool } from '../utils'
import { useRecapHelpers } from '../composables/recaps/useRecapHelpers'
import { DataRow } from '../types/components/data-rendering'
import { recapTaskData as taskProperties, useSharedRecap } from '../composables/recaps/useRecap'
import { checkMaintenance } from '../components/ModalMaintenance.vue'
import { RecapResponsesModel } from '../models/RecapReponsesModel'

// Translations
const TASK_EXECUTION_REASON = 'Task Execution'

export default /*#__PURE__*/_defineComponent({
  __name: 'PageTaskProfile',
  setup(__props) {

const { t } = useI18n({ useScope: 'global' })
const taskProfileLabel = (key: string) => t(`taskProfile.${key}`)

// Recaps
const { isSavingRecap, flagResponseComplete, flagResponseIncomplete, removeByTaskId } =
  RecapResponsesModel()
const { setUpComposable, resetRecap, recapId } = useSharedRecap()

// Task
const taskID = ref(useRoute().params.id as string)

const task = computed<Task | undefined>(() => useTaskProfiles().getTaskByTaskId(taskID.value))
const recapTask = ref<Task | undefined>()
onBeforeUpdate(async () => {
  date.value = inject('date', ref(new Date())).value
  taskID.value = useRoute().params.id as string
})

// Error log
onErrorCaptured((err, inst, info) => {
  logInfo('error', 'TaskProfileError', 'Error captured on the Task Profile Page', {
    err,
    inst,
    info,
  })
})

// Actions
const date = inject('date', ref(new Date()))
const { addChildTimer, stopActiveChildTimer, editChildData } = useTimers()
const startTask = checkMaintenance(async () => {
  if (!useTaskProfiles().canStartTask(task.value!.task_id)) {
    return void 0
  }

  // We need to add the recap into memory
  recapTask.value = task.value
  await nextTick()
  await useRecapStore().setRecapFromDatabase(
    recapTask.value!.recap_id.toString(),
    recapTask.value!.task_id.toString()
  )
  await setUpComposable(
    recapTask.value!.recap_id.toString(),
    'task',
    recapTask.value!.task_id.toString(),
    false,
    undefined,
    false
  )
  await flagResponseIncomplete(recapTask.value!.task_id.toString())

  const timestamp = new Date()
  timestamp.setFullYear(date.value.getFullYear())
  timestamp.setMonth(date.value.getMonth())
  timestamp.setDate(date.value.getDate())
  timestamp.setSeconds(0)
  timestamp.setMilliseconds(0)

  void addChildTimer(
    {
      type: useTaskProfiles().translateTaskType(task.value!.task_type_id) ?? task.value!.task_type,
      data: {
        complete: false,
        store_name: task.value!.store_name,
        store_id: task.value!.store_id,
        task_id: task.value!.task_id,
        recap_id: task.value!.recap_id,
        work_type_id: Number(task.value!.work_type_id),
        order_type_id: Number(task.value!.order_type_id),
        project_code: task.value!.project_code,
        revisit: task.value!.revisit,
      },
    },
    timestamp
  )
})
const pauseTask = checkMaintenance(async () => {
  const removeBlock = router.beforeEach(() => false)
  const timestamp = new Date()
  timestamp.setFullYear(date.value.getFullYear())
  timestamp.setMonth(date.value.getMonth())
  timestamp.setDate(date.value.getDate())
  timestamp.setSeconds(0)
  timestamp.setMilliseconds(0)
  stopActiveChildTimer(timestamp)

  // Clear task properties and reset the task
  taskProperties.value = {}
  await useTaskProfiles().resetTask(taskID.value, false, false)

  // Remove recap from store
  useRecapStore().removeRecapFromStore(task.value!.recap_id, task.value!.task_id)
  recapTask.value = undefined
  resetRecap()
  removeBlock()
})
const endTask = async (recapSaved: boolean, data: { debug: any }) => {
  const { updateTaskByMerge, undoTaskInvChanges, close, triggerTaskDataRef, getTaskIndex } =
    useTaskProfiles()
  const childTimer = { ...getActiveChildTimer.value }
  const currentTask = { ...task.value }
  const currentTaskId = currentTask.task_id
  const currentTaskIndex = getTaskIndex(currentTaskId!)
  console.log('End task info: ', { currentTask, currentTaskId, currentTaskIndex })
  //store the original, unedited state in a separate key, if not already done (to allow resets)
  if (!currentTask.original_state) {
    taskProperties.value['original_state'] = currentTask as Task
  }
  const excludeFromSync = !includeRecap.value
  taskProperties.value['exclude_from_sync'] = excludeFromSync
  taskProperties.value['executed_at'] = new Date()
  taskProperties.value['execution_latitude'] = !Array.isArray(childTimer.coordinates)
    ? childTimer!.coordinates?.lat
    : null
  taskProperties.value['execution_longitude'] = !Array.isArray(childTimer.coordinates)
    ? childTimer!.coordinates?.lng
    : null
  //Update task fields
  await updateTaskByMerge(taskProperties.value, currentTaskId!, currentTaskIndex)
  await close(currentTaskId!, currentTaskIndex)
  await triggerTaskDataRef({ writeTasks: false })

  // It is possible the rep resumed the task and there may be transactions present for task id, clear them
  await undoTaskInvChanges(currentTaskId!)

  // Adjust Inventory
  // If hardware deduction code makes sense, use selected inv item and create transactions
  // RAI Code (above) on task property is edited first: this will be used to validate deductions along w/ task_type_id
  // Copy the now updated task
  const task_copy = { ...task.value }
  try {
    const hardwareDeductionCodes = useTaskProfiles().taskHardwareDeductionCodes(
      currentTaskId!,
      task_copy
    )

    if (hardwareDeductionCodes && includeRecap.value) {
      // Keep a record of uuids associated to task, so we can undo them if needed
      const transactionIds: string[] = []
      for (const code of hardwareDeductionCodes) {
        /**
         * Coordinate transactions here -
         * if code is deducting from store, it needs to use prev deduction code
         * if code is deducting from rep, it needs to use inv item selection
         */
        let uuid

        if (Number(code.qty_to_store) < 0) {
          if (task.value?.prev_item_installed) {
            const partArray = JSON.parse(
              JSON.parse(task.value.prev_item_installed as string)
            ).filter((part: any) => Number(part.quantity) > 0)
            //only create transaction if there was a prev inv item.
            if (partArray && partArray.length) {
              uuid = await useInventory().createPartTransaction({
                code,
                task: task_copy,
                part_number: partArray[0]['parts_id'],
                reason: TASK_EXECUTION_REASON,
              })
            }
          }
        } else {
          if (inventoryItemSelection.value) {
            uuid = await useInventory().createPartTransaction({
              code,
              task: task_copy,
              part_number: inventoryItemSelection.value.name,
              reason: TASK_EXECUTION_REASON,
            })
          }
        }
        if (uuid) {
          transactionIds.push(uuid)
        }
      }
    }
  } catch (e) {
    captureException(`Hardware transactions failure: ${e}`)
  }

  // Child timer logic
  const parentIndex = getAllParentTimers.value.length - 1
  const childIndex = getAllParentTimers.value[parentIndex].children.length - 1
  try {
    editChildData(parentIndex, childIndex, {
      complete: true,
      exclude_from_sync: excludeFromSync,
    })
  } catch (e) {
    console.error(e)
    captureException(`Failure to edit task data on closing: ${e}`)
  }

  const timestamp = new Date()
  timestamp.setFullYear(date.value.getFullYear())
  timestamp.setMonth(date.value.getMonth())
  timestamp.setDate(date.value.getDate())
  timestamp.setSeconds(0)
  timestamp.setMilliseconds(0)
  stopActiveChildTimer(timestamp)

  // Extra context and logging for sentry
  // Check status close
  if (task.value?.task_status_id != 22 || task.value?.exclude_from_sync != excludeFromSync) {
    setContext('task', { ...task.value })
    setContext('newTaskData', { ...taskProperties.value })
    logInfo('error', 'Task data validation fails', `Task ${task.value?.task_id} failed to close`, {
      task: { ...task.value },
      newTaskData: { ...taskProperties.value },
    })
  }

  // Clear task properties
  taskProperties.value = {}

  // Save the task and clean up the recap
  await TaskModel().saveByIndex(currentTaskIndex, task_copy as DataRow)
  useRecapStore().removeRecapFromStore(task_copy.recap_id, task_copy.task_id)
  recapTask.value = undefined
  resetRecap()
  if (recapSaved) {
    // Only flag the response as completed if explictly told to
    await flagResponseComplete(task_copy.task_id as string)
  }
  isSavingRecap.value = false
}
const cancelTask = async (force = false) => {
  const removeBlock = router.beforeEach(() => false)
  // Remove the recap and its response if it exists
  if (recapTask.value?.task_id) {
    await removeByTaskId((recapTask.value as any)?.task_id)
  }
  useRecapStore().removeRecapFromStore(recapTask.value?.recap_id, recapTask.value?.task_id)
  recapTask.value = undefined
  resetRecap()

  // Reset the task
  const { resetTask } = useTaskProfiles()
  await resetTask(taskID.value)

  // Clear task properties
  taskProperties.value = {}
  if (force) {
    // This is to resolve the rare case in which a task cannot be uploaded
    // We still need to be able to remove the last timer, even though the task cannot be found
    const { stopActiveChildTimer } = useTimers()
    stopActiveChildTimer()
    await router.push('/')
  }
  removeBlock()
}

// Active States
const { getActiveParentTimer, getActiveChildTimer, getCurrentChildTime, getAllParentTimers } =
  storeToRefs(useTimers())
const thisTaskActive = computed(() => {
  if (!getActiveParentTimer.value) return false
  if (!getActiveChildTimer.value) return false
  if (getActiveChildTimer.value.data.task_id == task.value!.task_id) return true
  else return false
})
const thisStoreActive = computed(() => {
  if (!getActiveParentTimer.value) return false
  if (getActiveParentTimer.value.data.store_id == task.value!.store_id) return true
  else return false
})
const otherStoreActive = computed(() => {
  if (!getActiveParentTimer.value) return false
  if (getActiveParentTimer.value.data.store_id != task.value!.store_id) return true
  else return false
})
const noTaskActive = computed(() => {
  if (!getActiveChildTimer.value) return true
  else return false
})
const currentChildTime = computed(() => {
  return useFormatTime(getCurrentChildTime.value)
})
const thisTaskComplete = computed(() => {
  const data = findLast(
    getAllParentTimers.value.map((timer) => timer.children).flat(),
    (child: DeepReadonly<ChildTimer>) => {
      return child.data.task_id == taskID.value
    }
  )

  if (typeof data === 'undefined') return false
  else return data.data.complete && task.value?.task_status_id == 22
})
const thisTaskWasStarted = computed(() => {
  const data = findLast(
    getAllParentTimers.value.map((timer) => timer.children).flat(),
    (child: DeepReadonly<ChildTimer>) => {
      return child.data.task_id == taskID.value
    }
  )
  if (typeof data === 'undefined') return false
  else return true
})
const thisTaskTotalTime = computed(() => {
  return useFormatTime(
    getAllParentTimers.value
      .map((timer) => timer.children)
      .flat()
      .reduce((acc, curr) => {
        if (curr.data.task_id == taskID.value && !isNullish(curr.end_time)) {
          return acc + (curr.end_time.getTime() - curr.start_time.getTime())
        } else return acc
      }, 0),
    { format: 'h:mm', initTime: 'ms' }
  )
})
const startDatePassed = computed(() => {
  return new Date().getTime() >= new Date(task.value?.task_start_date || new Date()).getTime()
})

const taskStates = reactive({
  thisTaskActive,
  thisStoreActive,
  otherStoreActive,
  noTaskActive,
  currentChildTime,
  thisTaskComplete,
  thisTaskWasStarted,
  thisTaskTotalTime,
  startDatePassed,
})

// Display the camera icon if the task will require images
const { getRequireImageRaiCodesFromTask } = useRecapHelpers()
const displayCamera = computed(() => getRequireImageRaiCodesFromTask(task.value))

const router = useRouter()
const removeRouteGuard = router.beforeEach(() => {
  // Check if the task is not active before clearing the recap
  if (taskStates.noTaskActive) {
    resetRecap()
    // Clear task properties
    taskProperties.value = {}
  }
  errorSavingRecap.value = false
})
async function loadRecap() {
  errorSavingRecap.value = false
  resetRecap()
  await nextTick()
  await useRecapStore().setRecapFromDatabase(
    recapTask.value!.recap_id.toString(),
    recapTask.value!.task_id.toString()
  )
  await setUpComposable(
    recapTask.value!.recap_id.toString(),
    'task',
    recapTask.value!.task_id.toString(),
    false,
    undefined,
    false
  )
}
onMounted(async () => {
  if (taskStates.thisTaskActive && !recapTask.value) {
    recapTask.value = useTaskProfiles().getTaskByTaskId(taskID.value)
    await loadRecap()
  }
})
onBeforeUnmount(async () => {
  removeRouteGuard()

  // Clear task properties
  taskProperties.value = {}
})
watch(
  () => taskStates.thisTaskActive,
  async () => {
    if (taskStates.thisTaskActive && !recapTask.value) {
      recapTask.value = useTaskProfiles().getTaskByTaskId(taskID.value)
      await loadRecap()
    }
  }
)
watch(
  taskProperties,
  () => {
    // Print out the task properties as they change for debugging purposes
    console.log('Task properties: ', toRaw(taskProperties.value))
  },
  { deep: true }
)
const includeRecap = ref(true)
const errorSavingRecap = ref(false)
const recapDefinition = ref<typeof RecapDefinition | null>(null)
const endingRecap = ref(false)
const endTaskAndFetchRecap = checkMaintenance(async () => {
  // Prevent page navigation while task is ending
  const removeBlock = router.beforeEach(() => false)

  isSavingRecap.value = true
  errorSavingRecap.value = false
  if (recapDefinition.value && includeRecap.value) {
    endingRecap.value = true
    const id = JSON.parse(JSON.stringify(taskID.value))
    const currentRecapId = JSON.parse(JSON.stringify(recapId.value))
    const definition: any = { ...recapDefinition.value }
    let res: { validationError: boolean } | boolean = false
    try {
      const { saveRecap } = useRecapStore()
      res = await saveRecap(definition, id)
      if (typeof res === 'object' && 'validationError' in res) {
        errorSavingRecap.value = true
      } else if (!res) {
        throw new Error('Could not save recap response')
      }
    } catch (error) {
      logInfo('error', 'endTaskAndFetchRecap', 'Error saving recap', {
        error,
        recapDefinitionId: currentRecapId,
        task_id: id,
      })
      errorSavingRecap.value = true
      isSavingRecap.value = false
    }
    if (res === true) {
      const recapSubmittedEvent = new CustomEvent<{ success: boolean }>('recapSubmitted', {
        detail: {
          success: true,
        },
      })
      window.dispatchEvent(recapSubmittedEvent)
      await endTask(true, { debug: {} })
    } else if (res === false) {
      logInfo('error', 'endTaskAndFetchRecapSaveError', 'Error saving recap', {
        recapDefinition: recapDefinition.value,
        task_id: taskID.value,
      })
      errorSavingRecap.value = true
      isSavingRecap.value = false
    } else {
      // The recap had a validation error, which is handled elsewhere
      isSavingRecap.value = false
    }
  } else if (recapDefinition.value && !includeRecap.value) {
    // We still need to close out the recap locally, but we don't want that data to update on the portal
    endingRecap.value = true
    await endTask(false, { debug: {} })
  }
  endingRecap.value = false
  isSavingRecap.value = false
  removeBlock()
})

// Inventory Item Selection
const updateInventoryItem = (e: Event) => {
  console.log(e)
  inventoryItemSelection.value = e
}
const inventoryItemSelection = ref()
provide(
  'inventoryItemSelection',
  ref({
    inventoryItemSelection,
    taskProperties,
    task,
    updateInventoryItem,
  })
)

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_chip = _resolveComponent("q-chip")!
  const _component_q_badge = _resolveComponent("q-badge")!
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_q_btn_dropdown = _resolveComponent("q-btn-dropdown")!
  const _component_q_toggle = _resolveComponent("q-toggle")!
  const _directive_close_popup = _resolveDirective("close-popup")!

  return (task.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("p", _hoisted_2, [
          _createTextVNode(_toDisplayString(task.value.work_type) + " / " + _toDisplayString(task.value.task_type) + " @ ", 1),
          _createVNode(_component_router_link, {
            class: "text-primary",
            to: `/stores/${task.value.store_id}`
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(`${task.value.store_name} ${task.value.store_number}`), 1)
            ]),
            _: 1
          }, 8, ["to"])
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("h1", _hoisted_5, [
              _createTextVNode(_toDisplayString(task.value.artwork) + " ", 1),
              (displayCamera.value)
                ? (_openBlock(), _createBlock(_component_q_chip, {
                    key: 0,
                    square: ""
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_q_icon, {
                        size: "xs",
                        name: "camera_alt"
                      })
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (_unref(parseBool)(task.value.test_indicator))
                ? (_openBlock(), _createBlock(_component_q_chip, {
                    key: 1,
                    square: ""
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(taskProfileLabel('test_indicator')), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_6, [
            (
            taskStates.thisTaskActive &&
            taskStates.thisTaskWasStarted &&
            !taskStates.thisTaskComplete
          )
              ? (_openBlock(), _createBlock(_component_q_badge, {
                  key: 0,
                  color: "running",
                  class: "q-py-xs text-body2"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(`${taskProfileLabel('running')} ${taskStates.currentChildTime}`), 1)
                  ]),
                  _: 1
                }))
              : (
            taskStates.noTaskActive && taskStates.thisTaskWasStarted && !taskStates.thisTaskComplete
          )
                ? (_openBlock(), _createBlock(_component_q_badge, {
                    key: 1,
                    color: "info",
                    class: "q-py-xs text-body2"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(`${taskProfileLabel('paused')} ${taskStates.thisTaskTotalTime}`), 1)
                    ]),
                    _: 1
                  }))
                : (
            taskStates.noTaskActive &&
            !taskStates.thisTaskComplete &&
            !taskStates.thisTaskWasStarted
          )
                  ? (_openBlock(), _createBlock(_component_q_badge, {
                      key: 2,
                      color: "grey",
                      class: "q-py-xs text-body2"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(taskProfileLabel('open')), 1)
                      ]),
                      _: 1
                    }))
                  : (taskStates.thisTaskComplete)
                    ? (_openBlock(), _createBlock(_component_q_badge, {
                        key: 3,
                        color: "positive",
                        class: "q-py-xs text-body2"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(`${taskProfileLabel('completed')}`), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
          ])
        ]),
        _createVNode(TaskBase, { task: task.value }, null, 8, ["task"]),
        _createElementVNode("div", _hoisted_7, [
          (taskStates.startDatePassed)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                (!taskStates.thisStoreActive)
                  ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(taskProfileLabel('checkInToStart')), 1))
                  : _createCommentVNode("", true),
                (
            taskStates.thisStoreActive && taskStates.noTaskActive && !taskStates.thisTaskComplete
          )
                  ? (_openBlock(), _createBlock(_component_q_btn, {
                      key: 1,
                      "no-caps": "",
                      rounded: "",
                      color: "primary",
                      label: 
            taskStates.thisTaskWasStarted
              ? taskProfileLabel('resumeTask')
              : taskProfileLabel('startTask')
          ,
                      onClick: _unref(startTask)
                    }, null, 8, ["label", "onClick"]))
                  : _createCommentVNode("", true)
              ], 64))
            : (_openBlock(), _createElementBlock("span", _hoisted_9, " Cannot Start Task until " + _toDisplayString(_unref(useFormatDate)(new Date(task.value.task_start_date)).month()) + " " + _toDisplayString(_unref(useFormatDate)(new Date(task.value.task_start_date)).day()) + ".", 1))
        ]),
        (taskStates.thisStoreActive && taskStates.thisTaskComplete)
          ? (_openBlock(), _createBlock(_component_q_btn_dropdown, {
              key: 0,
              label: taskProfileLabel('resumeTask'),
              onClick: _unref(startTask),
              color: "primary",
              split: "",
              class: "q-mx-md"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_q_list, null, {
                  default: _withCtx(() => [
                    _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                      clickable: "",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => (cancelTask(false)))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_q_item_section, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_item_label, null, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_unref(t)('taskProfile.cancelTask')), 1)
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    })), [
                      [_directive_close_popup]
                    ])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["label", "onClick"]))
          : _createCommentVNode("", true),
        _withDirectives(_createElementVNode("div", null, [
          (recapTask.value)
            ? (_openBlock(), _createBlock(RecapDefinition, {
                ref_key: "recapDefinition",
                ref: recapDefinition,
                key: recapTask.value.task_id,
                "task-or-store-id": recapTask.value.task_id.toString(),
                "recap-id": recapTask.value.recap_id.toString(),
                "testing-recap": false
              }, null, 8, ["task-or-store-id", "recap-id"]))
            : _createCommentVNode("", true)
        ], 512), [
          [_vShow, 
        taskStates.thisStoreActive &&
        taskStates.thisTaskActive &&
        !taskStates.thisTaskComplete &&
        includeRecap.value
      ]
        ]),
        (taskStates.thisTaskActive && !taskStates.thisTaskComplete)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              _createElementVNode("div", _hoisted_10, [
                _createVNode(_component_q_btn_dropdown, {
                  split: "",
                  class: "q-mx-md",
                  loading: endingRecap.value
                }, {
                  label: _withCtx(() => [
                    _createElementVNode("div", {
                      onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(endTaskAndFetchRecap) && _unref(endTaskAndFetchRecap)(...args))),
                      class: "row items-center no-wrap"
                    }, _toDisplayString(_unref(t)('taskProfile.endTask')), 1)
                  ]),
                  default: _withCtx(() => [
                    _createVNode(_component_q_list, null, {
                      default: _withCtx(() => [
                        _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                          clickable: "",
                          onClick: _unref(pauseTask)
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_item_section, null, {
                              default: _withCtx(() => [
                                _createVNode(_component_q_item_label, null, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(t)('taskProfile.pauseTask')), 1)
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["onClick"])), [
                          [_directive_close_popup]
                        ]),
                        _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                          clickable: "",
                          onClick: _cache[2] || (_cache[2] = ($event: any) => (cancelTask(false)))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_item_section, null, {
                              default: _withCtx(() => [
                                _createVNode(_component_q_item_label, null, {
                                  default: _withCtx(() => [
                                    _createTextVNode(_toDisplayString(_unref(t)('taskProfile.cancelTask')), 1)
                                  ]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        })), [
                          [_directive_close_popup]
                        ])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["loading"]),
                _createVNode(_component_q_toggle, {
                  modelValue: includeRecap.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((includeRecap).value = $event))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)('taskProfile.includeRecap')), 1)
                  ]),
                  _: 1
                }, 8, ["modelValue"])
              ]),
              (errorSavingRecap.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(_unref(t)('taskProfile.errorSavingRecap')), 1))
                : _createCommentVNode("", true)
            ], 64))
          : _createCommentVNode("", true)
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_12, [
        _createElementVNode("div", _hoisted_13, _toDisplayString(_unref(t)('taskProfile.noTask')), 1),
        _createElementVNode("div", _hoisted_14, [
          _createElementVNode("div", _hoisted_15, [
            _createVNode(_component_q_btn, {
              onClick: _cache[4] || (_cache[4] = ($event: any) => (cancelTask(true)))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('taskProfile.cancelTask')), 1)
              ]),
              _: 1
            })
          ])
        ])
      ]))
}
}

})